import { fcfa } from '../Format'

/**
 * Graphiques SVG maison — transposés de `svgLineCA`, `svgDonut`, `svgHBar`
 * et de la jauge du tableau de bord HTML. Couleurs = variables CSS (dans un
 * `style`, où elles se résolvent), jamais l'émeraude claire pour du texte.
 */

export function CourbeCA({ lignes }: { lignes: { mois: string; ca: number }[] }) {
  if (lignes.length === 0) return <div className="ch-empty">Pas encore de produits reconnus</div>
  const W = 320
  const H = 150
  const P = 24
  const n = lignes.length
  const max = Math.max(1, ...lignes.map((r) => r.ca))
  const x = (i: number) => P + (n > 1 ? (i * (W - 2 * P)) / (n - 1) : (W - 2 * P) / 2)
  const y = (v: number) => H - P - (v / max) * (H - 2 * P)
  const chemin = lignes.map((r, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(r.ca).toFixed(1)}`).join(' ')
  const aire = `M${x(0).toFixed(1)} ${H - P} ${lignes.map((r, i) => `L${x(i).toFixed(1)} ${y(r.ca).toFixed(1)}`).join(' ')} L${x(n - 1).toFixed(1)} ${H - P} Z`
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" role="img" aria-label="Chiffre d'affaires reconnu par mois">
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} style={{ stroke: 'var(--border)' }} strokeWidth={1} />
      <path d={aire} style={{ fill: 'var(--gold-wash)' }} />
      <path d={chemin} fill="none" style={{ stroke: 'var(--gold-dark)' }} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />
      {lignes.map((r, i) => (
        <g key={r.mois}>
          <circle cx={x(i)} cy={y(r.ca)} r={2.8} style={{ fill: '#fff', stroke: 'var(--gold-dark)' }} strokeWidth={2}>
            <title>
              {r.mois} : {fcfa(r.ca)}
            </title>
          </circle>
          <text x={x(i)} y={H - 6} textAnchor="middle" fontSize={8.5} style={{ fill: 'var(--muted)' }}>
            {r.mois.slice(5)}/{r.mois.slice(2, 4)}
          </text>
        </g>
      ))}
    </svg>
  )
}

export interface Part {
  libelle: string
  valeur: number
  couleur: string
}

export function Anneau({ parts }: { parts: Part[] }) {
  const p = parts.filter((x) => x.valeur > 0)
  const total = p.reduce((s, x) => s + x.valeur, 0)
  if (!total) return <div className="ch-empty">Aucun produit</div>
  let a = -Math.PI / 2
  const R = 52
  const r = 31
  const cx = 60
  const cy = 60
  const segments = p.map((x) => {
    const ang = (x.valeur / total) * Math.PI * 2
    /* Un seul segment à 100 % : un arc de 360° est dégénéré en SVG. */
    const a2 = a + Math.min(ang, Math.PI * 2 - 0.0001)
    const grand = ang > Math.PI ? 1 : 0
    const d = `M${cx + R * Math.cos(a)} ${cy + R * Math.sin(a)} A${R} ${R} 0 ${grand} 1 ${cx + R * Math.cos(a2)} ${cy + R * Math.sin(a2)} L${cx + r * Math.cos(a2)} ${cy + r * Math.sin(a2)} A${r} ${r} 0 ${grand} 0 ${cx + r * Math.cos(a)} ${cy + r * Math.sin(a)} Z`
    a = a + ang
    return { d, ...x }
  })
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg viewBox="0 0 120 120" style={{ width: 118, flex: 'none' }} role="img" aria-label="Répartition du chiffre d'affaires par flux">
        {segments.map((sg) => (
          <path key={sg.libelle} d={sg.d} style={{ fill: `var(${sg.couleur})` }} />
        ))}
      </svg>
      <div style={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {p.map((x) => (
          <div className="ch-leg" key={x.libelle} style={{ width: '100%' }}>
            <span className="sw" style={{ background: `var(${x.couleur})` }} />
            {x.libelle} <b style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{fcfa(x.valeur)}</b>
          </div>
        ))}
      </div>
    </div>
  )
}

export function Barres({ items, vide }: { items: Part[]; vide: string }) {
  const it = items.filter((i) => i.valeur > 0)
  if (!it.length) return <div className="ch-empty">{vide}</div>
  const max = Math.max(1, ...it.map((i) => i.valeur))
  return (
    <>
      {it.map((i) => (
        <div className="hbar" key={i.libelle}>
          <div className="hbar-l" title={i.libelle}>
            {i.libelle}
          </div>
          <div className="hbar-t">
            <div className="hbar-f" style={{ width: `${((i.valeur / max) * 100).toFixed(1)}%`, background: `var(${i.couleur})` }} />
          </div>
          <div className="hbar-v">{fcfa(i.valeur)}</div>
        </div>
      ))}
    </>
  )
}

export function Jauge({ produits, charges, resultat }: { produits: number; charges: number; resultat: number }) {
  const max = Math.max(1, produits, charges)
  return (
    <>
      <div className="gauge">
        {[
          { l: 'Produits', v: produits, c: '--gold-dark' },
          { l: 'Charges', v: charges, c: '--err' },
        ].map((g) => (
          <div className="gauge-row" key={g.l}>
            <div className="gauge-l">{g.l}</div>
            <div className="gauge-t">
              <div className="gauge-f" style={{ width: `${((Math.max(0, g.v) / max) * 100).toFixed(1)}%`, background: `var(${g.c})` }} />
            </div>
            <div className="gauge-v">{fcfa(g.v)}</div>
          </div>
        ))}
      </div>
      <div className="dash-res">
        <span className="l">Résultat net</span>
        <span className="v" style={{ color: resultat >= 0 ? 'var(--green)' : 'var(--err)' }}>
          {fcfa(resultat)}
        </span>
      </div>
    </>
  )
}
